<template>
  <div class="space-y-6">
    <!-- Page Header -->
    <div>
      <h1 class="text-2xl font-semibold text-gray-100">Widget Customization</h1>
      <p class="mt-1 text-sm text-gray-400">Customize your search experience</p>
    </div>

    <!-- Status Bar -->
    <div class="bg-dark rounded-lg border border-gray-700 p-4 space-y-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3">
            <div class="flex items-center">
              <button
                type="button"
                @click="isWidgetActive = !isWidgetActive"
                class="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-dark"
                :class="isWidgetActive ? 'bg-primary' : 'bg-gray-700'"
                role="switch"
                :aria-checked="isWidgetActive"
              >
                <span
                  class="pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                  :class="isWidgetActive ? 'translate-x-5' : 'translate-x-0'"
                >
                  <span
                    class="absolute inset-0 flex h-full w-full items-center justify-center transition-opacity"
                    :class="isWidgetActive ? 'opacity-0 duration-100 ease-out' : 'opacity-100 duration-200 ease-in'"
                  >
                    <svg class="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 12 12">
                      <path d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </span>
                  <span
                    class="absolute inset-0 flex h-full w-full items-center justify-center transition-opacity"
                    :class="isWidgetActive ? 'opacity-100 duration-200 ease-in' : 'opacity-0 duration-100 ease-out'"
                  >
                    <svg class="h-3 w-3 text-primary" fill="currentColor" viewBox="0 0 12 12">
                      <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                    </svg>
                  </span>
                </span>
              </button>
              <span class="ml-3 text-sm font-medium text-gray-300">
                Widget is {{ isWidgetActive ? 'active' : 'inactive' }}
              </span>
            </div>
            <div class="h-4 w-px bg-gray-700"></div>
            <div class="flex items-center space-x-1">
              <div class="w-2 h-2 rounded-full" :class="isWidgetActive ? 'bg-green-500' : 'bg-gray-500'"></div>
              <span class="text-sm text-gray-400">Status: {{ isWidgetActive ? 'Live' : 'Disabled' }}</span>
            </div>
          </div>
          <button 
            @click="saveChanges"
            :disabled="isSaving"
            class="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark disabled:opacity-50 disabled:cursor-not-allowed"
            :class="[
              saveSuccess
                ? 'bg-emerald-500/10 text-emerald-500 ring-emerald-500 hover:bg-emerald-500/20'
                : 'bg-primary text-white ring-primary hover:bg-primary-dark'
            ]"
          >
            <svg
              v-if="isSaving"
              class="animate-spin h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <svg 
              v-else-if="saveSuccess"
              xmlns="http://www.w3.org/2000/svg" 
              class="h-4 w-4" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
            <span>{{ isSaving ? 'Saving...' : saveSuccess ? 'Saved!' : 'Save Changes' }}</span>
          </button>
        </div>

        <!-- Rules Panel -->
        <div class="border-t border-gray-700 pt-4">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-sm font-medium text-gray-200">Visibility Rules</h3>
            <div class="flex items-center space-x-2">
              <span class="text-sm text-gray-400">Combine rules with:</span>
              <div class="flex rounded-lg border border-gray-700 p-1">
                <button
                  v-for="op in ['AND', 'OR'] as LogicalOperator[]"
                  :key="op"
                  @click="customization.rules.operator = op"
                  :class="[
                    'px-3 py-1 text-sm font-medium rounded transition-colors',
                    customization.rules.operator === op
                      ? 'bg-primary text-white'
                      : 'text-gray-400 hover:bg-gray-700/50 hover:text-white'
                  ]"
                >
                  {{ op }}
                </button>
              </div>
              <button
                @click="customization.rules.conditions.push({ field: 'page_url' as RuleField, operator: 'equals' as RuleOperator, value: '' })"
                class="ml-4 px-3 py-1 text-sm font-medium text-gray-200 rounded-lg border border-gray-700 hover:border-primary/30 hover:bg-primary/10 hover:text-primary"
              >
                Add Rule
              </button>
            </div>
          </div>

          <!-- Rules List -->
          <div class="space-y-2">
            <div
              v-for="(rule, index) in customization.rules.conditions"
              :key="index"
              class="flex items-center gap-2"
            >
              <select
                v-model="rule.field"
                class="bg-dark border border-gray-700 rounded-md text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option
                  v-for="(label, field) in FIELD_LABELS"
                  :key="field"
                  :value="field"
                >
                  {{ label }}
                </option>
              </select>

              <select
                v-model="rule.operator"
                class="bg-dark border border-gray-700 rounded-md text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option
                  v-for="(label, op) in OPERATOR_LABELS"
                  :key="op"
                  :value="op"
                >
                  {{ label }}
                </option>
              </select>

              <input
                v-model="rule.value"
                type="text"
                class="flex-1 bg-dark border border-gray-700 rounded-md text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                :placeholder="'Enter ' + FIELD_LABELS[rule.field].toLowerCase()"
              >

              <button
                @click="customization.rules.conditions.splice(index, 1)"
                class="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-500/10"
                title="Remove rule"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </div>

            <!-- Empty State -->
            <div
              v-if="customization.rules.conditions.length === 0"
              class="text-center py-4 text-gray-400 text-sm border border-dashed border-gray-700 rounded-lg"
            >
              No rules added yet. Click "Add Rule" to start defining visibility conditions.
            </div>
          </div>
        </div>
      </div>

    <!-- Main Content -->
    <div class="bg-dark-secondary rounded-lg">

      <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <!-- Preview - Moved to left for better visibility -->
        <div class="lg:col-span-7 lg:sticky lg:top-6 space-y-6">
          <div class="bg-dark rounded-lg border border-gray-700 overflow-hidden">
            <!-- Preview Header -->
            <div class="flex items-center justify-between p-4 border-b border-gray-700">
              <h2 class="text-lg font-medium text-gray-100">Live Preview</h2>
              <div class="flex items-center space-x-1">
                <button
                  v-for="device in (['desktop', 'tablet', 'mobile'] as const)"
                  :key="device"
                  @click="previewDevice = device"
                  class="p-2 rounded-md text-sm font-medium transition-colors"
                  :class="[
                    previewDevice === device
                      ? 'bg-gray-700 text-gray-100'
                      : 'text-gray-400 hover:text-gray-300'
                  ]"
                >
                  {{ device }}
                </button>
              </div>
            </div>
            
            <!-- Preview Content -->
            <div class="relative w-full aspect-[4/3]" :class="{
              'max-w-sm mx-auto': previewDevice === 'mobile',
              'max-w-2xl mx-auto': previewDevice === 'tablet'
            }">
              <search-widget-preview
                :customization="customization"
                :device="previewDevice"
              />
            </div>
          </div>
        </div>

        <!-- Controls - Right side -->
        <div class="lg:col-span-5 space-y-4">
          <!-- Quick Themes -->
          <div class="bg-dark rounded-lg border border-gray-700 p-4">
            <div class="flex items-center justify-between mb-3">
              <h2 class="text-sm font-medium text-gray-100">Quick Themes</h2>
              <div class="h-px flex-1 bg-gray-700/50 mx-3"></div>
            </div>
            <div class="flex gap-2">
              <button
                v-for="theme in presetThemes"
                :key="theme.name"
                @click="applyTheme(theme)"
                class="group flex-1 relative px-3 py-2 rounded-md border transition-all overflow-hidden"
                :class="[
                  currentTheme
                    ? currentTheme === theme.name
                      ? 'border-primary/50 bg-primary/5 hover:bg-primary/10'
                      : 'border-gray-800 hover:border-gray-700 hover:bg-gray-800/30'
                    : 'border-gray-800 hover:border-gray-700 hover:bg-gray-800/30'
                ]"
              >
                <div class="flex items-center gap-2">
                  <!-- Color Preview -->
                  <div class="relative w-3 h-3 rounded-sm overflow-hidden">
                    <div class="absolute inset-0" 
                      :style="{ backgroundColor: theme.appearance.input.background }">
                    </div>
                    <div class="absolute bottom-0 right-0 w-1.5 h-1.5 rounded-tl"
                      :style="{ backgroundColor: theme.appearance.cta.background }">
                    </div>
                  </div>
                  
                <!-- Theme Name -->
                  <span class="text-xs font-medium" :class="[
                    currentTheme === theme.name
                      ? 'text-primary'
                      : 'text-gray-400 group-hover:text-gray-300'
                  ]">{{ theme.name }}</span>
                </div>

                <!-- Active Indicator -->
                <div v-if="currentTheme === theme.name" 
                  class="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-primary">
                </div>
              </button>
            </div>
          </div>

          <!-- Colors -->
          <div class="bg-dark rounded-lg border border-gray-700 p-4">
            <h2 class="text-lg font-medium text-gray-100 mb-4">Colors</h2>
            <div class="grid grid-cols-2 gap-4">
              <!-- Input Colors -->
              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Input Text</label>
                  <div class="relative group">
                    <div 
                      class="absolute inset-0 rounded-lg"
                      :style="{ backgroundColor: customization.appearance.input.color }"
                    ></div>
                    <input
                      v-model="customization.appearance.input.color"
                      type="color"
                      class="opacity-0 w-full h-12 cursor-pointer relative z-10"
                    >
                    <div class="absolute inset-0 rounded-lg border border-gray-700 pointer-events-none group-hover:border-gray-600 transition-colors"></div>
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Input Background</label>
                  <div class="relative group">
                    <div 
                      class="absolute inset-0 rounded-lg"
                      :style="{ backgroundColor: customization.appearance.input.background }"
                    ></div>
                    <input
                      v-model="customization.appearance.input.background"
                      type="color"
                      class="opacity-0 w-full h-12 cursor-pointer relative z-10"
                    >
                    <div class="absolute inset-0 rounded-lg border border-gray-700 pointer-events-none group-hover:border-gray-600 transition-colors"></div>
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Placeholder</label>
                  <div class="relative group">
                    <div 
                      class="absolute inset-0 rounded-lg"
                      :style="{ backgroundColor: customization.appearance.input.placeholder.color }"
                    ></div>
                    <input
                      v-model="customization.appearance.input.placeholder.color"
                      type="color"
                      class="opacity-0 w-full h-12 cursor-pointer relative z-10"
                    >
                    <div class="absolute inset-0 rounded-lg border border-gray-700 pointer-events-none group-hover:border-gray-600 transition-colors"></div>
                  </div>
                </div>
              </div>

              <!-- Accent Colors -->
              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Highlight</label>
                  <div class="relative group">
                    <div 
                      class="absolute inset-0 rounded-lg"
                      :style="{ backgroundColor: customization.appearance.input.highlight }"
                    ></div>
                    <input
                      v-model="customization.appearance.input.highlight"
                      type="color"
                      class="opacity-0 w-full h-12 cursor-pointer relative z-10"
                    >
                    <div class="absolute inset-0 rounded-lg border border-gray-700 pointer-events-none group-hover:border-gray-600 transition-colors"></div>
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-2">Button</label>
                  <div class="relative group">
                    <div 
                      class="absolute inset-0 rounded-lg"
                      :style="{ backgroundColor: customization.appearance.cta.background }"
                    ></div>
                    <input
                      v-model="customization.appearance.cta.background"
                      type="color"
                      class="opacity-0 w-full h-12 cursor-pointer relative z-10"
                    >
                    <div class="absolute inset-0 rounded-lg border border-gray-700 pointer-events-none group-hover:border-gray-600 transition-colors"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Text -->
          <div class="bg-dark rounded-lg border border-gray-700 p-4">
            <h2 class="text-lg font-medium text-gray-100 mb-4">Text</h2>
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">Font Size</label>
                <select
                  v-model="customization.appearance.input.size"
                  class="w-full bg-dark border border-gray-700 rounded-md text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="14px">Small</option>
                  <option value="16px">Medium</option>
                  <option value="18px">Large</option>
                </select>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-300 mb-2">Placeholder Text</label>
                <input
                  v-model="customization.appearance.input.placeholder.text"
                  type="text"
                  class="w-full bg-dark border border-gray-700 rounded-md text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Enter placeholder text..."
                >
              </div>
            </div>
          </div>

          <!-- Prompt Suggestions -->
          <div class="bg-dark rounded-lg border border-gray-700 p-4">
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-lg font-medium text-gray-100">Prompt Suggestions</h2>
              <button
                @click="customization.promptSuggestions.push({ value: '' })"
                class="p-2 text-gray-400 hover:text-gray-300 rounded-md hover:bg-gray-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>
            <div class="space-y-2">
              <div 
                v-for="(suggestion, index) in customization.promptSuggestions" 
                :key="index"
                class="group flex items-center space-x-2"
              >
                <input
                  v-model="suggestion.value"
                  type="text"
                  class="flex-1 bg-dark border border-gray-700 rounded-md text-gray-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Enter suggestion..."
                >
                <button
                  v-if="customization.promptSuggestions.length > 1"
                  @click="customization.promptSuggestions.splice(index, 1)"
                  class="p-2 text-gray-600 hover:text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import SearchWidgetPreview from '@/components/search/SearchWidgetPreview.vue'
import { useOrgStore } from '@/stores/org'

type RuleField = keyof typeof FIELD_LABELS
type RuleOperator = keyof typeof OPERATOR_LABELS
type LogicalOperator = 'AND' | 'OR'

interface Rule {
  field: RuleField
  operator: RuleOperator
  value: string
}

interface Customization {
  appearance: {
    thread: {
      color: string
      background: string
      font: string
      linkColor: string
    }
    input: {
      color: string
      background: string
      size: string
      highlight: string
      placeholder: {
        text: string
        color: string
      }
    }
    cta: {
      background: string
    }
  }
  promptSuggestions: Array<{ value: string }>
  defaultFont: string
  rules: {
    operator: LogicalOperator
    conditions: Rule[]
  }
}

interface Theme {
  name: string
  appearance: Customization['appearance']
}

const presetThemes = [
  {
    name: 'Light',
    appearance: {
      thread: {
        color: '#000000',
        background: '#ffffff',
        font: '',
        linkColor: '#a1a1a1'
      },
      input: {
        color: '#000000',
        background: '#ffffff',
        size: '16px',
        highlight: '#e5e7eb',
        placeholder: {
          text: 'Ask anything...',
          color: '#808080'
        }
      },
      cta: {
        background: '#814ac8'
      }
    }
  },
  {
    name: 'Dark',
    appearance: {
      thread: {
        color: '#ffffff',
        background: '#1f2937',
        font: '',
        linkColor: '#a1a1a1'
      },
      input: {
        color: '#ffffff',
        background: '#111827',
        size: '16px',
        highlight: '#374151',
        placeholder: {
          text: 'Ask anything...',
          color: '#9ca3af'
        }
      },
      cta: {
        background: '#814ac8'
      }
    }
  },
  {
    name: 'Warm',
    appearance: {
      thread: {
        color: '#000000',
        background: '#fbe7d5',
        font: '',
        linkColor: '#a1a1a1'
      },
      input: {
        color: '#000000',
        background: '#ffffff',
        size: '16px',
        highlight: '#f97316',
        placeholder: {
          text: 'Ask anything...',
          color: '#808080'
        }
      },
      cta: {
        background: '#f97316'
      }
    }
  },
  {
    name: 'Minimal',
    appearance: {
      thread: {
        color: '#000000',
        background: '#ffffff',
        font: '',
        linkColor: '#a1a1a1'
      },
      input: {
        color: '#000000',
        background: '#f8fafc',
        size: '16px',
        highlight: '#e2e8f0',
        placeholder: {
          text: 'Ask anything...',
          color: '#808080'
        }
      },
      cta: {
        background: '#64748b'
      }
    }
  }
]

const FIELD_LABELS = {
  page_url: 'Page URL',
  page_title: 'Page Title'
} as const

const OPERATOR_LABELS = {
  equals: 'Equals',
  not_equals: 'Not Equals',
  contains: 'Contains',
  not_contains: 'Not Contains'
} as const

// Default configuration
const defaultConfig: Customization = {
  appearance: {
    thread: {
      color: '#000000',
      background: '#fbe7d5',
      font: '',
      linkColor: '#a1a1a1'
    },
    input: {
      color: '#000000',
      background: '#ffffff',
      size: '16px',
      highlight: '#a1a1a1',
      placeholder: {
        text: 'Ask anything...',
        color: '#808080'
      }
    },
    cta: {
      background: '#814ac8'
    }
  },
  promptSuggestions: [
    { value: 'Best topper for side sleepers?' },
    { value: 'What is the best pillow for side sleepers?' },
    { value: 'Sore neck and hot sleeper' },
    { value: 'What is the best mattress for back pain?' }
  ],
  defaultFont: 'https://storage.googleapis.com/greet-public/fonts/Alliance.woff',
  rules: {
    operator: 'AND' as LogicalOperator,
    conditions: []
  }
}

const orgStore = useOrgStore()
const isSaving = ref(false)
const saveSuccess = ref(false)
const saveError = ref<string | null>(null)

const isWidgetActive = ref(false)
const currentTheme = ref('')
const previewDevice = ref<'desktop' | 'tablet' | 'mobile'>('desktop')
const customization = ref<Customization>(structuredClone(defaultConfig))

// Load initial widget config
onMounted(async () => {
  console.log('Component mounted, initializing...')
  
  // Make sure org data is loaded
  if (!orgStore.orgData) {
    console.log('No org data, fetching...')
    await orgStore.fetchOrgData()
    orgStore.watchOrgData() // Setup live updates
  }
  
  console.log('Current org data:', orgStore.orgData)
  
  if (orgStore.orgData?.widget) {
    console.log('Loading existing widget config:', orgStore.orgData.widget)
    const widget = orgStore.orgData.widget
    
    // Reset to default config first
    customization.value = structuredClone(defaultConfig)
    
    // Update active state
    isWidgetActive.value = widget.is_active
    
    // Update rules
    customization.value.rules.operator = widget.visibility.operator as LogicalOperator
    customization.value.rules.conditions = widget.visibility.rules.map(rule => ({
      field: rule.field as RuleField,
      operator: rule.operator as RuleOperator,
      value: rule.value
    }))
    
    // Update styles if they exist
    if (widget.widget_styles) {
      const styles = widget.widget_styles
      
      if (styles.appearance) {
        customization.value.appearance = styles.appearance
        updateCurrentTheme()
      }
      
      if (styles.promptSuggestions) {
        customization.value.promptSuggestions = styles.promptSuggestions
      }
      
      if (styles.defaultFont) {
        customization.value.defaultFont = styles.defaultFont
      }
    }
  } else {
    console.log('No existing widget config found, using default configuration')
    customization.value = structuredClone(defaultConfig)
  }
})

// Watch for org data changes
watch(() => orgStore.orgData?.widget, (newWidget) => {
  if (newWidget) {
    console.log('Widget config updated:', newWidget)
    
    // Only update if we're not currently saving
    if (!isSaving.value) {
      // Reset to default config first
      customization.value = structuredClone(defaultConfig)
      
      // Update active state
      isWidgetActive.value = newWidget.is_active
      
      // Update rules
      customization.value.rules.operator = newWidget.visibility.operator as LogicalOperator
      customization.value.rules.conditions = newWidget.visibility.rules.map(rule => ({
        field: rule.field as RuleField,
        operator: rule.operator as RuleOperator,
        value: rule.value
      }))
      
      // Update styles if they exist
      if (newWidget.widget_styles) {
        const styles = newWidget.widget_styles
        
        if (styles.appearance) {
          customization.value.appearance = styles.appearance
        }
        
        if (styles.promptSuggestions) {
          customization.value.promptSuggestions = styles.promptSuggestions
        }
        
        if (styles.defaultFont) {
          customization.value.defaultFont = styles.defaultFont
        }
      }
    }
  }
}, { deep: true })

// Watch for any color value changes to update theme selection
watch([
  () => customization.value.appearance.thread.color,
  () => customization.value.appearance.thread.background,
  () => customization.value.appearance.thread.linkColor,
  () => customization.value.appearance.input.color,
  () => customization.value.appearance.input.background,
  () => customization.value.appearance.input.highlight,
  () => customization.value.appearance.input.placeholder.color,
  () => customization.value.appearance.cta.background
], () => {
  if (!isSaving.value) {
    console.log('Color value changed, checking theme match...')
    updateCurrentTheme()
  }
})

const transformToWidgetConfig = () => {
  return {
    is_active: isWidgetActive.value,
    visibility: {
      operator: customization.value.rules.operator,
      rules: customization.value.rules.conditions
    },
    widget_styles: {
      appearance: customization.value.appearance,
      promptSuggestions: customization.value.promptSuggestions,
      defaultFont: customization.value.defaultFont
    }
  }
}

const saveChanges = async () => {
  console.log('Starting save process...')
  isSaving.value = true
  saveSuccess.value = false
  saveError.value = null

  try {
    const widgetConfig = transformToWidgetConfig()
    console.log('Widget config to save:', widgetConfig)
    console.log('Current org ID:', orgStore.orgData?.id)
    
    if (!orgStore.orgData?.id) {
      throw new Error('No org ID found')
    }
    
    await orgStore.updateWidgetConfig(widgetConfig)
    console.log('Save successful')
    saveSuccess.value = true
    setTimeout(() => {
      saveSuccess.value = false
    }, 2000)
  } catch (err: any) {
    saveError.value = err.message
    console.error('Error saving widget config:', err)
    alert('Failed to save: ' + err.message) // Temporary debug alert
  } finally {
    isSaving.value = false
  }
}


const isThemeMatching = (theme: Theme): boolean => {
  const current = customization.value.appearance
  const themeAppearance = theme.appearance
  
  // Helper function to compare values with type safety
  const compareValues = (a: string, b: string): boolean => {
    // Convert to lowercase for case-insensitive comparison of hex colors
    return a.toLowerCase() === b.toLowerCase()
  }
  
  // Check all color values match exactly
  return (
    compareValues(current.thread.color, themeAppearance.thread.color) &&
    compareValues(current.thread.background, themeAppearance.thread.background) &&
    compareValues(current.thread.linkColor, themeAppearance.thread.linkColor) &&
    compareValues(current.input.color, themeAppearance.input.color) &&
    compareValues(current.input.background, themeAppearance.input.background) &&
    compareValues(current.input.highlight, themeAppearance.input.highlight) &&
    compareValues(current.input.placeholder.color, themeAppearance.input.placeholder.color) &&
    compareValues(current.cta.background, themeAppearance.cta.background)
  )
}

const updateCurrentTheme = () => {
  const matchingTheme = presetThemes.find(theme => isThemeMatching(theme))
  currentTheme.value = matchingTheme?.name || ''
}

const applyTheme = (theme: Theme) => {
  // Create a deep copy of the theme appearance to avoid reference issues
  const newAppearance = JSON.parse(JSON.stringify(theme.appearance))
  
  // Update the appearance values
  customization.value = {
    ...customization.value,
    appearance: newAppearance
  }
  
  // Theme name will be updated by the watcher when colors are verified to match
  console.log('Applied theme:', theme.name)
}
</script> 